import { Cancel, CheckCircle } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { submitApproval } from 'lib/api/approvals';
import CustomButton from 'lib/designSystem/Button';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useComiteeTabContext } from './ComiteeTabProvider';
import { ApprovalRejectionDialogProps } from './types';

const ApprovalRejectionDialog: React.FC<ApprovalRejectionDialogProps> = ({
  open,
  type,
  onCancel,
  setOpen,
  userRoles,
}) => {
  const {
    requestType,
    requestId,
    comiteeType,
    setSelectedRoleId,
    selectedRoleId,
    refreshRequest,
  } = useComiteeTabContext();

  const [reason, setReason] = useState('');
  const isValidate =
    !reason.trim() ||
    !selectedRoleId ||
    !Array.isArray(userRoles) ||
    userRoles.length === 0;

  const dispatch = useDispatch();

  const { mutate: submitApprovalMutation } = submitApproval(
    dispatch,
    requestType,
    requestId,
    comiteeType,
  );

  const handleClose = () => {
    setOpen(false);
    setReason('');
  };

  const handleConfirm = async () => {
    if (isValidate) return;

    submitApprovalMutation(
      {
        decision: type,
        roleId: selectedRoleId,
        message: reason,
      },
      {
        onSuccess: () => void refreshRequest(),
      },
    );
    handleClose();
  };

  const handleCancel = () => {
    onCancel();
    handleClose();
  };

  const isApprove = type === 'APPROVED';
  const title = isApprove ? 'Aprobar Solicitud' : 'Rechazar Solicitud';
  const description = isApprove
    ? 'Confirma que la solicitud cumple con todos los criterios establecidos.'
    : 'Indica el motivo por el cual rechazas esta solicitud.';
  const buttonColor = isApprove ? 'success' : 'error';
  const buttonText = isApprove ? 'Aprobar' : 'Rechazar';
  const icon = isApprove ? <CheckCircle /> : <Cancel />;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="approval-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        id="approval-dialog-title"
        sx={{
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: isApprove
              ? 'rgba(76, 175, 80, 0.1)'
              : 'rgba(244, 67, 54, 0.1)',
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, {
            sx: {
              fontSize: '1.2rem',
              color: isApprove ? 'success.main' : 'error.main',
            },
          })}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          {description}
        </Typography>

        {Array.isArray(userRoles) && userRoles.length > 0 ? (
          <FormControl margin="normal" fullWidth error={!selectedRoleId}>
            <InputLabel id="sign-as-label">Firmas como</InputLabel>
            <Select
              labelId="sign-as-label"
              id="sign-as-select"
              value={selectedRoleId ?? ''}
              label="Firmas como"
              onChange={(e) => setSelectedRoleId(e.target.value as number)}
              error={!selectedRoleId}
            >
              {userRoles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
            {!selectedRoleId && (
              <Typography variant="caption" color="error">
                Debe seleccionar un rol para continuar
              </Typography>
            )}
          </FormControl>
        ) : (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            No hay roles disponibles para firmar
          </Typography>
        )}

        <TextField
          autoFocus
          label="Razón"
          type="text"
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            isApprove
              ? 'Describe por qué apruebas esta solicitud...'
              : 'Describe por qué rechazas esta solicitud...'
          }
          error={!reason.trim()}
          helperText={!reason.trim() ? 'La razón es requerida' : ''}
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <CustomButton onClick={handleCancel} color="secondary">
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={handleConfirm}
          color={buttonColor}
          disabled={isValidate}
        >
          {buttonText}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalRejectionDialog;
